import { useState, useEffect, useCallback } from 'react';
import { countLocalPendingWork } from '../../offline/local-pending-work';
import { networkService } from '@/services/network.service';
import { storageQuotaService, type StorageQuotaStatus } from '../../offline/storage-quota.service';
import {
  syncOrchestratorService,
  type SyncOrchestratorState,
  type SyncPhase,
} from '../../offline/sync-orchestrator.service';
import FaIcon from '@/components/common/FaIcon';
import { ArrowPathIcon, CheckCircleIcon, ChevronUpIcon, CloudIcon, XMarkIcon } from '../icons';
import Button, { IconButton } from '@/components/common/Button';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { offlineResetService } from '../../offline/offline-reset.service';
import { useToastStore } from '@/store/toast.store';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ServiceSyncStatus {
  name: string;
  pending: number;
  lastSynced: string | null;
  error: string | null;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function CloudOffIcon({ className }: { className?: string }) {
  return (
    <FaIcon icon="cloud-arrow-down" className={className} />
  );
}

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <FaIcon icon="triangle-exclamation" className={className} />
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function useStorageQuota(): StorageQuotaStatus | null {
  const [status, setStatus] = useState<StorageQuotaStatus | null>(
    storageQuotaService.getStatus()
  );

  useEffect(() => {
    void storageQuotaService.checkQuota().then(setStatus);
    return storageQuotaService.addListener(setStatus);
  }, []);

  return status;
}

// ─── Hook: useSyncStatus ─────────────────────────────────────────────────────

function useSyncStatus() {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const [syncState, setSyncState] = useState<SyncOrchestratorState>(
    syncOrchestratorService.getState()
  );
  const [services, setServices] = useState<ServiceSyncStatus[]>([]);
  const [totalPending, setTotalPending] = useState(0);
  const phase: SyncPhase = syncState.phase;
  const lastGlobalSync = syncState.lastSyncAt
    ? new Date(syncState.lastSyncAt).toISOString()
    : null;

  // Refresh pending counts from IndexedDB
  const refreshCounts = useCallback(async () => {
    try {
      // One list for the pill and the MAUI upgrade prompt (offline/local-pending-work.ts).
      // Counting a table loosely (`!is_synced`, not the index) matters: a
      // boolean-false row has no index entry, so an indexed count hid unsent
      // GPS tracks, shelter rosters and ICS forms behind a green "Synced" pill,
      // and the Discard dialog (which quotes this total) deleted them unseen.
      const {
        pendingMutations,
        unsyncedAssessments,
        unsyncedPhotos,
        unsyncedSupplyRequests,
        unsyncedOfflineSupplyRequests,
        unsyncedBreadcrumbs,
        unsyncedBoardPosts,
        unsyncedUnitLogs,
        unsyncedActivityEntries,
        unsyncedSnapshots,
        unsyncedPersonnelPositions,
        unsyncedTasks,
        unsyncedInfrastructure,
        unsyncedFemaCosts,
        unsyncedIncidents,
        unsyncedForms,
        unsyncedCheckins,
      } = await countLocalPendingWork();

      const serviceList: ServiceSyncStatus[] = [
        {
          name: 'Damage Assessments',
          pending: unsyncedAssessments + unsyncedPhotos,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Tasks',
          pending: unsyncedTasks,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Supply Requests',
          pending: unsyncedSupplyRequests + unsyncedOfflineSupplyRequests,
          lastSynced: null,
          error: null,
        },
        {
          name: 'PDA Breadcrumbs',
          pending: unsyncedBreadcrumbs,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Board Posts',
          pending: unsyncedBoardPosts,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Unit Logs',
          pending: unsyncedUnitLogs + unsyncedActivityEntries,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Infrastructure',
          pending: unsyncedInfrastructure,
          lastSynced: null,
          error: null,
        },
        {
          name: 'FEMA Costs',
          pending: unsyncedFemaCosts,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Incidents',
          pending: unsyncedIncidents,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Drone Snapshots',
          pending: unsyncedSnapshots,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Personnel Positions',
          pending: unsyncedPersonnelPositions,
          lastSynced: null,
          error: null,
        },
        {
          name: 'ICS Forms',
          pending: unsyncedForms,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Shelter Check-ins',
          pending: unsyncedCheckins,
          lastSynced: null,
          error: null,
        },
        {
          name: 'Other Mutations',
          pending: pendingMutations,
          lastSynced: null,
          error: null,
        },
      ];

      // Only show services that have or recently had pending items
      const relevant = serviceList.filter(s => s.pending > 0 || s.error);
      setServices(relevant.length > 0 ? relevant : serviceList.slice(0, 3));

      const total =
        pendingMutations +
        unsyncedAssessments +
        unsyncedPhotos +
        unsyncedSupplyRequests +
        unsyncedOfflineSupplyRequests +
        unsyncedBreadcrumbs +
        unsyncedBoardPosts +
        unsyncedUnitLogs +
        unsyncedActivityEntries +
        unsyncedSnapshots +
        unsyncedPersonnelPositions +
        unsyncedTasks +
        unsyncedInfrastructure +
        unsyncedFemaCosts +
        unsyncedIncidents +
        unsyncedForms +
        unsyncedCheckins;

      setTotalPending(total);
    } catch (err) {
      console.warn('[SyncStatus] Failed to refresh counts:', err);
    }
  }, []);

  const syncNow = useCallback(async () => {
    const result = await syncOrchestratorService.syncNow(true);
    if (result.errors.length > 0) {
      setServices(prev =>
        prev.map(s => ({
          ...s,
          error:
            result.errors.find(e => e.toLowerCase().includes(s.name.toLowerCase())) || s.error,
        }))
      );
    }
    await refreshCounts();
  }, [refreshCounts]);

  // Subscribe to orchestrator state for phase/lastSyncAt updates.
  useEffect(() => {
    return syncOrchestratorService.addListener(setSyncState);
  }, []);

  // Track online/offline transitions for the pill colour. The orchestrator
  // owns the actual reconnect-sync schedule app-wide; we only mirror state.
  useEffect(() => {
    const removeListener = networkService.addListener((connected) => {
      setIsOnline(connected);
    });
    return removeListener;
  }, []);

  // After every orchestrator-driven sync, refresh local pending counts.
  useEffect(() => {
    if (syncState.phase === 'done' || syncState.phase === 'error') {
      void refreshCounts();
    }
  }, [syncState.phase, syncState.lastSyncAt, refreshCounts]);

  // Poll pending counts every 10 seconds
  useEffect(() => {
    refreshCounts();
    const interval = setInterval(refreshCounts, 10000);
    return () => clearInterval(interval);
  }, [refreshCounts]);

  return {
    isOnline,
    phase,
    services,
    totalPending,
    lastGlobalSync,
    syncNow,
    refreshCounts,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SyncStatusIndicator() {
  const [expanded, setExpanded] = useState(false);
  const { isOnline, phase, services, totalPending, lastGlobalSync, syncNow, refreshCounts } =
    useSyncStatus();
  const quota = useStorageQuota();
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      const cleared = await offlineResetService.discardPending();
      useToastStore
        .getState()
        .success(`Discarded ${cleared} pending ${cleared === 1 ? 'record' : 'records'}.`);
      setShowDiscardConfirm(false);
      await refreshCounts();
    } catch (err) {
      console.error('[SyncStatus] discard failed:', err);
      useToastStore.getState().error('Failed to discard pending data.');
    } finally {
      setIsDiscarding(false);
    }
  };

  // Determine pill color
  const pillColor = !isOnline
    ? 'bg-red-600'
    : phase === 'error'
      ? 'bg-red-600'
      : phase === 'syncing'
        ? 'bg-primary-600'
        : totalPending > 0
          ? 'bg-yellow-500'
          : 'bg-green-600';

  const pillTextColor = totalPending > 0 && isOnline && phase !== 'syncing' && phase !== 'error'
    ? 'text-theme-primary'
    : 'text-white';

  // Pill label
  const pillLabel = !isOnline
    ? 'Offline'
    : phase === 'syncing'
      ? 'Syncing...'
      : phase === 'error'
        ? 'Sync Error'
        : totalPending > 0
          ? `${totalPending} pending`
          : 'Synced';

  // Pill icon
  const PillIcon = !isOnline
    ? CloudOffIcon
    : phase === 'syncing'
      ? ArrowPathIcon
      : phase === 'error'
        ? ExclamationIcon
        : totalPending > 0
          ? CloudIcon
          : CheckCircleIcon;

  return (
    <div className="relative z-40 shrink-0">
      {/* Collapsed Pill */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg touch-manipulation transition-all ${pillColor} ${pillTextColor}`}
        aria-label={`Sync status: ${pillLabel}. Tap to ${expanded ? 'collapse' : 'expand'}.`}
      >
        <PillIcon
          className={`h-4 w-4 shrink-0 ${phase === 'syncing' ? 'animate-spin' : ''}`}
        />
        <span className="text-xs font-semibold whitespace-nowrap">{pillLabel}</span>
        <ChevronUpIcon
          className={`h-3 w-3 shrink-0 transition-transform ${expanded ? '' : 'rotate-180'}`}
        />
      </button>

      {/* Expanded Panel */}
      {expanded && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-surface-elevated rounded-xl shadow-2xl border border-theme-primary overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  !isOnline ? 'bg-red-500' : totalPending > 0 ? 'bg-yellow-400' : 'bg-green-500'
                }`}
              />
              <span className="text-sm font-semibold text-theme-primary">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            <IconButton icon={<XMarkIcon className="h-4 w-4" />} label="Close sync panel" onClick={() => setExpanded(false)} variant="ghost" size="sm" />
          </div>

          {/* Storage Warning (only when at or above 70% used) */}
          {quota?.available && quota.isWarning && (
            <div
              className={`flex items-start gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700 ${
                quota.isCritical
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-yellow-50 dark:bg-yellow-900/20'
              }`}
            >
              <ExclamationIcon
                className={`h-4 w-4 shrink-0 mt-0.5 ${
                  quota.isCritical ? 'text-red-500' : 'text-yellow-600 dark:text-yellow-400'
                }`}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={`text-xs font-semibold ${
                    quota.isCritical
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-yellow-700 dark:text-yellow-300'
                  }`}
                >
                  {quota.isCritical ? 'Storage almost full' : 'Storage filling up'}
                </p>
                <p className="text-[10px] text-theme-secondary mt-0.5">
                  {formatBytes(quota.usageBytes)} of {formatBytes(quota.quotaBytes)} used
                  {' '}({Math.round(quota.percentUsed * 100)}%).
                  {quota.isCritical
                    ? ' Old cached data will be cleared to protect unsynced records.'
                    : ' Sync soon to avoid eviction.'}
                </p>
              </div>
            </div>
          )}

          {/* Service List */}
          <div className="px-4 py-2 max-h-60 overflow-y-auto">
            {services.map(svc => (
              <div
                key={svc.name}
                className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-700/50 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-theme-secondary truncate">
                    {svc.name}
                  </p>
                  {svc.error && (
                    <p role="alert" className="text-[10px] text-red-500 truncate">{svc.error}</p>
                  )}
                </div>
                <div className="ml-3 shrink-0">
                  {svc.pending > 0 ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
                      {svc.pending}
                    </span>
                  ) : svc.error ? (
                    <ExclamationIcon className="h-4 w-4 text-red-500" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  )}
                </div>
              </div>
            ))}

            {services.length === 0 && (
              <p className="text-xs text-theme-tertiary py-3 text-center">
                No offline data
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-theme-tertiary">
                Last sync: {formatRelativeTime(lastGlobalSync)}
              </span>
              <div className="flex items-center gap-2">
                {totalPending > 0 && (
                  <Button
                    onClick={() => setShowDiscardConfirm(true)}
                    disabled={phase === 'syncing' || isDiscarding}
                    variant="ghost"
                    size="sm"
                    icon={<FaIcon icon="trash-can" className="h-3.5 w-3.5" />}
                  >
                    Discard
                  </Button>
                )}
                <Button
                  onClick={() => {
                    syncNow();
                  }}
                  disabled={!isOnline || phase === 'syncing'}
                  variant="primary"
                  size="sm"
                  loading={phase === 'syncing'}
                  icon={<ArrowPathIcon className={`h-3.5 w-3.5 ${phase === 'syncing' ? 'animate-spin' : ''}`} />}
                >
                  {phase === 'syncing' ? 'Syncing...' : 'Sync Now'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        onClose={() => !isDiscarding && setShowDiscardConfirm(false)}
        onConfirm={handleDiscard}
        title="Discard pending uploads?"
        message={`This permanently deletes ${totalPending} pending ${
          totalPending === 1 ? 'record' : 'records'
        } that haven't synced. Cached data is kept. This cannot be undone.`}
        confirmLabel="Discard"
        variant="warning"
        isLoading={isDiscarding}
      />
    </div>
  );
}
